import { Component } from 'preact'
import { isInputEl } from '../../libs/typeGuards'
import Icon from '../assets'
import algoliasearch from 'algoliasearch/lite'
import AutoSuggestTags from './AutoSuggestTags'
import globals from '../../utils/globals'
import { common } from '../common'
// @ts-expect-error
import Downshift from 'downshift/preact'
import Phrase from '../../libs/translationsUtils/phrase'
import translations from '../../libs/translationsUtils/translations'
import { getSearchToken, Credentials } from '../../services/algoliaService'

interface Tag {
  id?: number
  name: string
}

type TagList = Tag[]

interface Props {
  isEdit: boolean
  inputName: string
  inputId?: string
  selectedPublicTags?: string
  canEndUserAddPublicTags: boolean
  onChange?: (i: string[]) => void
  iconSize?: number
}
interface TagHit {
  id: number
  name: string
  count: number
  _highlightResult: {
    name: {
      value: string
    }
  }
}
interface State {
  publicTagList: TagHit[]
  selectedPublicTags: TagList
  selectedTagsSet: Set<string>
  searchText: string
  index?: algoliasearch.Index
}

export default class PublicTagSelect extends Component<Props, State> {
  tagInput: HTMLInputElement | undefined
  dropDown: HTMLDivElement | undefined

  constructor (props: Props) {
    super(props)

    const selectedTagNames: TagList = props.selectedPublicTags?.length ? props.selectedPublicTags.split(',').map(t => ({ name: t })) : []
    const selectedTagsSet = new Set(selectedTagNames.map(t => t.name.toLowerCase()))

    this.state = {
      publicTagList: [],
      selectedPublicTags: selectedTagNames,
      selectedTagsSet: selectedTagsSet,
      searchText: ''
    }

    this.isTagSelected = this.isTagSelected.bind(this)
    this.selectPublicTag = this.selectPublicTag.bind(this)
    this.cb = this.cb.bind(this)
  }

  cb (data: Credentials) {
    const client = algoliasearch(data.client_id, data.token)
    this.setState({ index: client.initIndex(`${globals('communityId')}-tags`) })
  }

  async componentDidMount () {
    await getSearchToken(this.cb)

    this.state.index.search('').then(({ hits }: { hits: TagHit[] }) => {
      this.setState({ publicTagList: hits })
    })
  }

  componentDidUpdate (prevProps: Readonly<Props>) {
    if (this.props.selectedPublicTags !== prevProps.selectedPublicTags) {
      const selectedTagNames: TagList = this.props.selectedPublicTags?.length ? this.props.selectedPublicTags.split(',').map(t => ({ name: t })) : []
      const selectedTagsSet = new Set(selectedTagNames.map(t => t.name.toLowerCase()))

      this.setState({
        selectedPublicTags: selectedTagNames,
        selectedTagsSet: selectedTagsSet
      })
    }
  }

  updateSearchText (e: KeyboardEvent) {
    if (e.code === 'Escape') {
      this.tagInput?.blur()
      return
    }

    const target = e?.target
    if (!isInputEl(target)) {
      return
    }

    const value = target.value
    if (e.code === 'Backspace' && !this.state.searchText.length) {
      this.removeLastTag()
    }

    this.refreshSuggestions(value)
  }

  refreshSuggestions (searchText: string) {
    this.state.index.search(searchText, { restrictSearchableAttributes: ['name'] }).then(({ hits }: { hits: TagHit[] }) => {
      this.setState({ publicTagList: hits, searchText: searchText })
      this.tagInput?.focus()
    })
  }

  isTagSelected (tag: string) {
    return this.state.selectedTagsSet.has(tag.toLowerCase())
  }

  selectPublicTag (tag: Tag, remainFocusOnInput: boolean = true) {
    const { selectedPublicTags, selectedTagsSet } = this.state
    if (this.isTagSelected(tag.name)) {
      return
    }

    selectedTagsSet.add(tag.name.toLowerCase())
    selectedPublicTags.push(tag)

    this.setState({
      selectedPublicTags: selectedPublicTags,
      selectedTagsSet: selectedTagsSet,
      searchText: ''
    })

    if (this.props.onChange) {
      const tagsList = selectedPublicTags.map(tag => { return tag.name })
      this.props.onChange(tagsList)
    }

    if (remainFocusOnInput) {
      this.refreshSuggestions('')
    }
  }

  removeLastTag () {
    const { selectedPublicTags } = this.state

    if (!selectedPublicTags.length) {
      return
    }

    const lastSelected = selectedPublicTags[selectedPublicTags.length - 1]
    this.removePublicTag(lastSelected)
    this.refreshSuggestions('')
  }

  removePublicTag (tag: Tag) {
    const { selectedPublicTags, selectedTagsSet } = this.state

    if (!this.isTagSelected(tag.name)) {
      return
    }

    selectedTagsSet.delete(tag.name.toLowerCase())
    const index = selectedPublicTags.indexOf(tag)
    selectedPublicTags.splice(index, 1)

    if (this.props.onChange) {
      const tagsList = selectedPublicTags.map(tag => { return tag.name })
      this.props.onChange(tagsList)
    }

    this.setState({ selectedPublicTags: selectedPublicTags, selectedTagsSet: selectedTagsSet })
  }

  handleFocusChange (openMenu: Function, inputValue: string) {
    this.refreshSuggestions(inputValue)
    openMenu()
  }

  onCancelEditPublicTags () {
    const selectedTagNames: TagList = this.props.selectedPublicTags?.length ? this.props.selectedPublicTags.split(',').map(t => ({ name: t })) : []
    const selectedTagsSet = new Set(selectedTagNames.map(t => t.name.toLowerCase()))
    this.setState({
      selectedPublicTags: selectedTagNames,
      selectedTagsSet: selectedTagsSet,
      searchText: ''
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  render ({ isEdit, inputName, inputId, canEndUserAddPublicTags, iconSize }: Props, { publicTagList, selectedPublicTags, searchText, index }: State) {
    const tags = publicTagList.filter(tag => {
      return !this.isTagSelected(tag.name)
    })
    const closeIconSize = iconSize ?? 16
    return (
      <div><Downshift
        defaultHighlightedIndex={0}
        initialHighlightedIndex={0}
        onChange={(selection: Tag) => this.selectPublicTag({ name: selection.name })}
        stateReducer={(state: State, changes: { type: Downshift.stateChangeTypes.clickItem }) => {
          switch (changes.type) {
            case Downshift.stateChangeTypes.keyDownEnter:
            case Downshift.stateChangeTypes.clickItem:
            case Downshift.stateChangeTypes.keyDownEscape:
              return {
                ...changes,
                inputValue: ''
              }
            default:
              return changes
          }
        }}
        itemToString={(tag?: Tag) => (tag ? tag.name : '')}
           >{({
             getInputProps,
             getItemProps,
             getMenuProps,
             isOpen,
             openMenu,
             closeMenu,
             inputValue,
             highlightedIndex
           }: Downshift) => (
             <div className='public-tag-container qa-public-tag-container'>
               <div className={`public-tag-wrapper ${isEdit ? 'public-tag-edit-wrapper' : ''}`}>
                 {isEdit
                   ? (selectedPublicTags.length
                       ? (
                           selectedPublicTags.map((tag) => (
                             <input key={`unselected-${tag.name}`} type='hidden' name={inputName} value={tag.name} />
                           )))
                       : null)
                   : <input type='hidden' name={inputName} id={inputId} value={selectedPublicTags.map(t => t.name).join(',')} />}
                 {
              selectedPublicTags.length
                ? (selectedPublicTags.map((selected) => {
                    return (
                      <div key={`selected-${selected.name}`} className='public-tag_selected-tag' onClick={() => this.removePublicTag(selected)}>
                        <div className='public-tag_selected-tag-name'>{selected.name}</div>
                        <div
                          className='public-tag_selected-remove-button'
                        >
                          <Icon icon='close' width={closeIconSize} height={closeIconSize} color='currentColor' />
                        </div>
                      </div>
                    )
                  }))
                : null
            }
                 <div className='public-tag-input-wrapper'>
                   <input
                     title=''
                     ref={input => { this.tagInput = input }}
                     class='form-control'
                     placeholder={translations.translate('Forum', 'tags.add')}
                     type='text'
                     {...getInputProps({
                       onFocus: () => this.handleFocusChange(openMenu, inputValue),
                       onKeyUp: (e: KeyboardEvent) => this.updateSearchText(e)
                     })}
                // has to be outside to overwrite whatever getInputProps would put onBlur
                     onBlur={() => closeMenu()}
                   />
                 </div>
                 {
              isOpen && <div class='public-tag-list-container' ref={element => { this.dropDown = element }}>
                <DropDown
                  tags={tags}
                  searchText={searchText}
                  canEndUserAddPublicTags={canEndUserAddPublicTags}
                  isTagSelected={this.isTagSelected}
                  getMenuProps={getMenuProps}
                  getItemProps={getItemProps}
                  highlightedIndex={highlightedIndex}
                />
              </div>
            }
               </div>
               {common.isModuleActive('publicTagAutoSuggestion') && common.isModuleActive('publicTagDestination') &&
                 <AutoSuggestTags isTagSelected={this.isTagSelected} selectPublicTag={this.selectPublicTag} />}
             </div>
           )}
      </Downshift>
        {isEdit
          ? (<div className='public-tag-edit-buttons'><button className='btn btn--cancel js-collapsable--trigger btn--S qa-button-tags-cancel' data-toggle='tags' onClick={() => this.onCancelEditPublicTags()}><Phrase p='Cancel' m='Forum' /></button>
            <button className='btn btn--S btn--cta save-public-tag qa-button-tags-save' type='submit'><Phrase p='Save' m='Forum' /></button></div>)
          : null}
      </div>
    )
  }
}

const DropDown = ({ tags, canEndUserAddPublicTags, isTagSelected, searchText, getMenuProps, getItemProps, highlightedIndex }: {
  tags: TagHit[]
  searchText: string
  isTagSelected: (tag: string) => boolean
  canEndUserAddPublicTags: boolean
  getMenuProps: Function
  getItemProps: Function
  highlightedIndex: number
}) => {
  // eslint-disable-next-line
  let options = []
  const showCreatePublicTagOption = canEndUserAddPublicTags && !tags.some(tag => tag.name.toLowerCase() === searchText.toLowerCase())
  if (tags.length > 0) {
    options.push(...tags.map((item, index) => {
      const modifiedIndex = showCreatePublicTagOption ? index + 1 : index
      return (
      // eslint-disable-next-line react/jsx-key
        <li
          className='public-tag-list-item'
          {...
        getItemProps({
          key: item.name,
          index: modifiedIndex,
          item,
          class: highlightedIndex === modifiedIndex ? 'highlight-selected-option public-tag-list-item' : 'public-tag-list-item'
        })
        }
        >
          <span>{item.name}</span>
          <span className='usage-count'>{item.count}</span>
        </li>
      )
    }))
  } else if (!canEndUserAddPublicTags || !isTagSelected(searchText)) {
    options.push(<li className='public-tag_not-found'><Phrase p='live.search.no.result.found.text' m='Forum' /> {searchText}</li>)
  }
  if (showCreatePublicTagOption) {
    options.unshift(<AddNewPublicTag
      searchText={searchText}
      highlightedIndex={highlightedIndex}
      getItemProps={getItemProps} isTagSelected={isTagSelected}
                    />)
  }

  return (
    <ul {...getMenuProps()}>
      {options}
    </ul>
  )
}

const AddNewPublicTag = ({ searchText, isTagSelected, getItemProps, highlightedIndex }: {
  searchText: string
  isTagSelected: (tag: string) => boolean
  getItemProps: Function
  highlightedIndex: number
}) => {
  if (searchText.length === 0) {
    return null
  }

  if (isTagSelected(searchText)) {
    return <li className='public-tag-list-item existing-tag'><Phrase p='tags.already.added' m='Forum' /></li>
  }

  return (
    <li
      className='public-tag-list-item new-tag'
      {...getItemProps({
        key: searchText,
        index: 0,
        item: { name: searchText },
        class: highlightedIndex === 0 ? 'highlight-selected-option public-tag-list-item new-tag' : 'public-tag-list-item new-tag'
      })}
    >
      <Phrase p='tags.add.new' m='Forum' /> '{searchText}'
    </li>
  )
}

export { Tag }
